import { cn } from '@quadralize/utils'
import { AltArrowLeft, AltArrowRight } from '@solar-icons/react'
import * as React from 'react'
import {
  DAYS_PER_WEEK,
  FULL_PCT,
  MINS_PER_HOUR,
  MOBILE_AUTO_SCROLL_OFFSET,
  MOBILE_EVENT_CARD_VERTICAL_PADDING,
  MOBILE_EVENT_OVERLAP_TOTAL_WIDTH_PCT,
  MOBILE_PLACEHOLDER_EVERY_NTH_ROW,
  MOBILE_SKELETON_ROWS,
  MOBILE_SLOT_H,
  NOW_INTERVAL_MS,
} from './constants'
import { EventCard } from './event-card'
import { SchedulerDateTitle, SchedulerHeaderControls } from './header-controls'
import { ResourceTabs } from './resource-tabs'
import { SlotRow } from './slot-row'
import type { SchedulerProps } from './types'
import {
  buildSlots,
  formatDate,
  nowLabel,
  nowMin,
  positionEvents,
  startOfDay,
  toEndMin,
  toIso,
  toMin,
} from './utils'
import { WeekDayTabs } from './week-day-tabs'

// ─── Types ────────────────────────────────────────────────────────────────────

type MobileSchedulerProps = Omit<SchedulerProps, 'hourHeight'>

// ─── Component ────────────────────────────────────────────────────────────────

export const MobileScheduler = ({
  className,
  date,
  view = 'day',
  activeResourceId,
  startTime = 7,
  endTime = 23,
  slotDuration = 30,
  resources,
  events,
  blockedSlots = [],
  businessHours,
  isLoading = false,
  onSlotClick,
  onEventClick,
  onDateChange,
  onTodayClick,
  onDatePick,
  onEventHover,
  onBlockedSlotClick,
  onViewChange,
  showViewSwitcher = false,
  showCurrentTime = true,
  showOccupancyIndicators = true,
  showResourceTabs = true,
  showTodayButton = true,
  todayButtonLabel = 'Hoje',
  showDatePicker = true,
  datePickerLabel = 'Selecionar data',
  density = 'comfortable',
  onActiveResourceChange,
  renderHeaderExtras,
  renderEvent,
  renderSlot,
  ...props
}: MobileSchedulerProps): React.JSX.Element => {
  const isCompactDensity = density === 'compact'
  // ── Active resource/date state ────────────────────────────────────────────
  const [activeResId, setActiveResId] = React.useState(activeResourceId ?? resources[0]?.id ?? '')
  const [activeWeekIso, setActiveWeekIso] = React.useState<string>(toIso(date))

  // Sync if parent changes
  React.useEffect(() => {
    if (activeResourceId) setActiveResId(activeResourceId)
  }, [activeResourceId])

  React.useEffect(() => {
    if (!activeResId) return
    onActiveResourceChange?.(activeResId)
  }, [activeResId, onActiveResourceChange])

  // Sync selected day when week navigation changes the date prop
  React.useEffect(() => {
    setActiveWeekIso(toIso(date))
  }, [date])

  const navigatePeriod = React.useCallback(
    (direction: -1 | 1): void => {
      const nextDate = new Date(date)

      if (view === 'week') {
        nextDate.setDate(nextDate.getDate() + direction * DAYS_PER_WEEK)
      } else {
        nextDate.setDate(nextDate.getDate() + direction)
      }

      onDateChange?.(nextDate)
    },
    [date, onDateChange, view],
  )

  const prevPeriod = React.useCallback((): void => {
    navigatePeriod(-1)
  }, [navigatePeriod])

  const nextPeriod = React.useCallback((): void => {
    navigatePeriod(1)
  }, [navigatePeriod])

  const goToToday = React.useCallback((): void => {
    const todayDate = startOfDay(new Date())
    onDateChange?.(todayDate)
    onTodayClick?.(todayDate, view)
  }, [onDateChange, onTodayClick, view])

  // ── Live clock ────────────────────────────────────────────────────────────
  const [curMin, setCurMin] = React.useState(nowMin)
  const [curLabel, setCurLabel] = React.useState(nowLabel)
  React.useEffect(() => {
    if (!showCurrentTime) return

    const t = setInterval(() => {
      setCurMin(nowMin())
      setCurLabel(nowLabel())
    }, NOW_INTERVAL_MS)
    return () => clearInterval(t)
  }, [showCurrentTime])

  const today = new Date()
  const todayIso = toIso(today)
  const isToday =
    view === 'week' ? activeWeekIso === todayIso : today.toDateString() === date.toDateString()
  const isCurrentPeriod =
    view === 'day'
      ? isToday
      : view === 'week'
        ? activeWeekIso === todayIso
        : date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()

  // ── Filter events for the current column ─────────────────────────────────
  const columnEvents = React.useMemo(() => {
    if (view === 'week') {
      return events.filter((e) => e.resourceId === activeResId && e.date === activeWeekIso)
    }

    const activeDayIso = toIso(date)
    return events.filter(
      (e) => e.resourceId === activeResId && (!e.date || e.date === activeDayIso),
    )
  }, [events, activeResId, activeWeekIso, view, date])
  const activeDayIso = React.useMemo(() => toIso(date), [date])

  const columnBlocked = React.useMemo(
    () =>
      blockedSlots.filter((slot) => {
        const matchesResource = !slot.resourceId || slot.resourceId === activeResId
        if (!matchesResource) return false

        const visibleDate = view === 'week' ? activeWeekIso : activeDayIso
        return !slot.date || slot.date === visibleDate
      }),
    [blockedSlots, activeDayIso, activeResId, activeWeekIso, view],
  )

  // ── Build time slots ──────────────────────────────────────────────────────
  const slots = React.useMemo(
    () => buildSlots(startTime, endTime, slotDuration, columnEvents, columnBlocked, businessHours),
    [startTime, endTime, slotDuration, columnEvents, columnBlocked, businessHours],
  )

  // ── Auto-scroll to now on mount ───────────────────────────────────────────
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const autoScrollRef = React.useRef({ curMin, startTime })
  autoScrollRef.current = { curMin, startTime }

  React.useEffect(() => {
    if (!showCurrentTime) return
    if (!isToday || !scrollRef.current) return
    const { curMin: cm, startTime: st } = autoScrollRef.current
    const nowOffset = cm - st * MINS_PER_HOUR
    if (nowOffset < 0) return
    const px = (nowOffset / slotDuration) * MOBILE_SLOT_H
    const frame = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({
        top: Math.max(0, px - MOBILE_AUTO_SCROLL_OFFSET),
        behavior: 'smooth',
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [isToday, showCurrentTime, slotDuration])

  // ── Header date string ────────────────────────────────────────────────────
  const headerLabel =
    view === 'week' ? formatDate(new Date(`${activeWeekIso}T00:00:00`)) : formatDate(date)

  const activeResource = resources.find((r) => r.id === activeResId)
  const headerExtras = renderHeaderExtras?.({
    view,
    activeDate: view === 'week' ? new Date(`${activeWeekIso}T00:00:00`) : date,
    isMobile: true,
    resources,
    activeResourceId: activeResId,
  })
  const columnId = activeResId
  const columnDate = view === 'week' ? activeWeekIso : undefined
  const visibleDate = view === 'week' ? new Date(`${activeWeekIso}T00:00:00`) : date
  const headerControlButtonClass = cn(
    'rounded-lg font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-base',
    isCompactDensity ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]',
  )
  const viewSwitcherContainerClass = cn(
    'inline-flex w-full items-stretch gap-0 rounded-none border-0 bg-transparent shadow-none',
  )
  const viewSwitcherButtonClass = cn(
    'flex-1 rounded-none border-r border-border-default font-medium text-center transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-0 last:border-r-0',
    isCompactDensity ? 'px-0 py-2 text-[10px]' : 'px-0 py-2.5 text-[11px]',
  )

  // ── Positioned events (absolute layer) ────────────────────────────────────
  const positionedEvents = React.useMemo(() => positionEvents(columnEvents), [columnEvents])

  return (
    <div
      className={cn(
        'flex flex-col w-full h-full rounded-xl border border-border-default bg-bg-base overflow-hidden',
        className,
      )}
      {...props}
    >
      {/* ── Top header: date + now ── */}
      <div
        className={cn(
          'flex items-center gap-2 border-b border-border-default bg-bg-base/95 shrink-0 supports-backdrop-filter:backdrop-blur-md',
          isCompactDensity ? 'px-3.5 py-2' : 'px-4 py-2.5',
        )}
      >
        <button
          type="button"
          onClick={prevPeriod}
          aria-label={view === 'week' ? 'Semana anterior' : 'Dia anterior'}
          className="shrink-0 p-1 rounded-lg text-text-tertiary transition-colors cursor-pointer hover:bg-bg-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
        >
          <AltArrowLeft size={16} />
        </button>

        <div className="min-w-0 flex-1">
          <SchedulerDateTitle
            label={headerLabel}
            selectedDate={date}
            view={view}
            showDatePicker={showDatePicker}
            datePickerLabel={datePickerLabel}
            triggerClassName={cn(
              'max-w-full rounded-lg px-1.5 py-0.5 font-semibold capitalize text-text-primary transition-colors cursor-pointer hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
              isCompactDensity ? 'text-[11px]' : 'text-xs',
            )}
            textClassName={cn(
              'font-semibold text-text-primary capitalize',
              isCompactDensity ? 'text-[11px]' : 'text-xs',
            )}
            onDateChange={(selectedDate) => onDateChange?.(selectedDate)}
            onDatePick={onDatePick}
          />
          {(activeResource || (showCurrentTime && isToday)) && (
            <div
              className={cn(
                'mt-0.5 flex items-center gap-2 text-text-tertiary',
                isCompactDensity ? 'text-[9px]' : 'text-[10px]',
                showDatePicker && 'px-1.5',
              )}
            >
              {activeResource && (
                <>
                  {activeResource.icon}
                  <span>{activeResource.name}</span>
                </>
              )}
              {showCurrentTime && isToday && (
                <div className="inline-flex items-center gap-1.5 font-medium text-text-secondary tabular-nums">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                  </span>
                  <span>{curLabel}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <SchedulerHeaderControls
            view={view}
            showTodayButton={showTodayButton}
            todayButtonLabel={todayButtonLabel}
            isCurrentPeriod={isCurrentPeriod}
            onTodayClick={goToToday}
            density={density}
            buttonClassName={headerControlButtonClass}
            activeClassName="bg-primary/12 text-primary shadow-sm"
            inactiveClassName="text-text-tertiary hover:bg-bg-muted/80 hover:text-text-primary"
          />

          <button
            type="button"
            onClick={nextPeriod}
            aria-label={view === 'week' ? 'Próxima semana' : 'Próximo dia'}
            className="p-1 rounded-lg text-text-tertiary transition-colors cursor-pointer hover:bg-bg-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
          >
            <AltArrowRight size={16} />
          </button>
        </div>
      </div>

      {headerExtras && (
        <div
          className={cn(
            'border-b border-border-default bg-bg-subtle/90 supports-backdrop-filter:backdrop-blur-md',
            isCompactDensity ? 'px-2 py-1.5' : 'px-3 py-2',
          )}
        >
          {headerExtras}
        </div>
      )}

      {showViewSwitcher && (
        <div
          className={cn(
            'border-b border-border-default bg-bg-subtle/90 supports-backdrop-filter:backdrop-blur-md',
            'px-0 py-0',
          )}
        >
          <SchedulerHeaderControls
            view={view}
            showViewSwitcher={showViewSwitcher}
            showTodayButton={false}
            todayButtonLabel={todayButtonLabel}
            isCurrentPeriod={isCurrentPeriod}
            onTodayClick={goToToday}
            onViewChange={onViewChange}
            density={density}
            wrapperClassName="w-full"
            containerClassName={viewSwitcherContainerClass}
            buttonClassName={viewSwitcherButtonClass}
            activeClassName="bg-primary/12 text-primary border-primary/35 border-y first:border-l last:border-r"
            inactiveClassName="text-text-tertiary hover:bg-bg-muted/80 hover:text-text-primary"
          />
        </div>
      )}

      {/* ── Week day tabs ── */}
      {view === 'week' && (
        <WeekDayTabs
          date={date}
          activeIso={activeWeekIso}
          density={density}
          onSelect={setActiveWeekIso}
        />
      )}

      {/* ── Resource tabs ── */}
      {showResourceTabs && resources.length > 1 && (
        <ResourceTabs
          resources={resources}
          activeId={activeResId}
          events={events}
          showOccupancyIndicators={showOccupancyIndicators}
          density={density}
          startTime={startTime}
          endTime={endTime}
          onSelect={setActiveResId}
        />
      )}

      {/* ── Loading skeleton ── */}
      {isLoading ? (
        <div className="flex-1 overflow-hidden py-2 px-3 flex flex-col gap-2">
          {Array.from({ length: MOBILE_SKELETON_ROWS }).map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton items never reorder
              key={i}
              className="flex gap-2 items-start"
              style={{ minHeight: `${MOBILE_SLOT_H}px` }}
            >
              {/* Time label placeholder */}
              <div className="w-11 shrink-0 flex justify-end pt-1.5">
                {i % 2 === 0 && <div className="w-8 h-2.5 rounded bg-bg-elevated animate-pulse" />}
              </div>
              {/* Content placeholder */}
              <div className="flex-1 pt-1">
                {i % MOBILE_PLACEHOLDER_EVERY_NTH_ROW === 0 && (
                  <div
                    className="rounded-lg bg-bg-elevated animate-pulse"
                    style={{
                      height: `${MOBILE_SLOT_H * (i === 0 ? 2 : 1) - MOBILE_EVENT_CARD_VERTICAL_PADDING}px`,
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Scrollable agenda ── */
        <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* Business hours banner */}
          {businessHours && (
            <div className="px-4 py-2 bg-bg-subtle border-b border-border-subtle flex items-center gap-2">
              <span className="text-[10px] text-text-tertiary">
                Horário de atendimento:{' '}
                <span className="font-semibold text-text-secondary">
                  {String(businessHours.start).padStart(2, '0')}:00 –{' '}
                  {String(businessHours.end).padStart(2, '0')}:00
                </span>
              </span>
            </div>
          )}

          {/* Slot list and absolute events layer */}
          <div className="py-1 relative min-h-0">
            {/* Absolute events layer */}
            <div className="absolute inset-y-0 left-11 right-0 pointer-events-none z-10 py-1">
              {positionedEvents.map((ev) => {
                const startOffset = toMin(ev.startTime) - startTime * MINS_PER_HOUR
                const dur =
                  Math.min(toEndMin(ev.startTime, ev.endTime), endTime * MINS_PER_HOUR) -
                  toMin(ev.startTime)
                const top = (startOffset / slotDuration) * MOBILE_SLOT_H
                const height = (dur / slotDuration) * MOBILE_SLOT_H
                const widthPct =
                  ev.totalColumns > 1
                    ? MOBILE_EVENT_OVERLAP_TOTAL_WIDTH_PCT / ev.totalColumns
                    : FULL_PCT
                const leftPct = (ev.column / ev.totalColumns) * FULL_PCT

                return (
                  <div key={ev.id} className="pointer-events-auto">
                    <EventCard
                      event={ev}
                      top={top}
                      height={height}
                      widthPct={widthPct}
                      leftPct={leftPct}
                      onEventClick={onEventClick}
                      onEventHover={onEventHover}
                      view={view}
                      activeDate={visibleDate}
                      resource={resources.find((resource) => resource.id === ev.resourceId)}
                      isMobile
                      density={density}
                      renderEvent={renderEvent}
                    />
                  </div>
                )
              })}
            </div>

            {slots.map((slot, idx) => {
              const isHourBoundary = slot.minutes % MINS_PER_HOUR === 0
              const isNowSlot =
                showCurrentTime &&
                isToday &&
                curMin >= slot.minutes &&
                curMin < slot.minutes + slotDuration

              return (
                <SlotRow
                  key={slot.timeStr}
                  slot={slot}
                  columnId={columnId}
                  columnDate={columnDate}
                  showLabel={isHourBoundary || idx === 0}
                  isNow={isNowSlot}
                  activeDate={visibleDate}
                  view={view}
                  resource={activeResource}
                  slotDuration={slotDuration}
                  onSlotClick={onSlotClick}
                  onBlockedSlotClick={onBlockedSlotClick}
                  density={density}
                  renderSlot={renderSlot}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

MobileScheduler.displayName = 'MobileScheduler'
